import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { buildAppMeta, SITE_URL } from "@/lib/seo";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Wordmark } from "@/components/Wordmark";
import { toast } from "sonner";

export const Route = createFileRoute("/reset-password")({
  head: () => ({
    meta: buildAppMeta({
      title: "Set a new password — CoachQuick",
      description: "Choose a new password for your CoachQuick coach account.",
      path: "/reset-password",
    }),
    links: [{ rel: "canonical", href: `${SITE_URL}/reset-password` }],
  }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const nav = useNavigate();
  const [ready, setReady] = useState<"checking" | "ok" | "expired">("checking");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let settled = false;
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" || session) {
        settled = true;
        setReady("ok");
      }
    });

    supabase.auth.getSession().then(({ data }) => {
      if (data.session) {
        settled = true;
        setReady("ok");
      } else {
        // Give the SDK a moment to parse the recovery hash from the URL.
        setTimeout(() => {
          if (!settled) setReady((s) => (s === "ok" ? s : "expired"));
        }, 1500);
      }
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (pw.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (pw !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: pw });
      if (error) throw error;
      toast.success("Password updated. You're signed in.");
      nav({ to: "/app" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not update password");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-secondary/30 px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="mb-8 flex items-center justify-center" aria-label="CoachQuick home">
          <Wordmark className="text-2xl font-extrabold tracking-tight" />
        </Link>
        <Card className="p-8 shadow-elevated">
          {ready === "checking" && (
            <p className="text-sm text-muted-foreground">Checking your reset link…</p>
          )}

          {ready === "expired" && (
            <div>
              <h1 className="text-2xl font-bold tracking-tight">This link expired</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                Password reset links are single use and time limited. Request a fresh one and we'll
                send it right over.
              </p>
              <Button asChild className="mt-6 w-full">
                <Link to="/auth" search={{ mode: "reset" } as never}>
                  Request a new link
                </Link>
              </Button>
            </div>
          )}

          {ready === "ok" && (
            <>
              <h1 className="text-2xl font-bold tracking-tight">Set a new password</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Pick something you'll actually remember this time.
              </p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <Label htmlFor="password">New password</Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="new-password"
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    required
                    minLength={8}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="confirm">Confirm password</Label>
                  <Input
                    id="confirm"
                    type="password"
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    minLength={8}
                    className="mt-1.5"
                  />
                </div>
                <Button type="submit" className="w-full" disabled={busy}>
                  {busy ? "Updating…" : "Update password"}
                </Button>
              </form>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
